import { lazy } from 'react';
import { adminRoutes } from './adminRoutes';
import { LioUserRole, BrokerageUserRole } from '@lio/rule-engine';
import { IRoute } from '@router/routes/Route.interface';
import { UserRoles, allButLioSales, internalRoles, allRoles } from '@models/user';
import { paymentRoutes } from './paymentRoutes';

// NOTE: could add default exports back to make this less.... ugly
// more notes: agreed lol
const BindQuotePage = lazy(() =>
  import('../../pages/BindQuote/BindQuotePage').then((module) => ({ default: module.BindQuotePage })),
);

const CustomerDetailsPage = lazy(() =>
  import('../../pages/CustomerDetails/CustomerDetailsPage').then((module) => ({ default: module.CustomerDetailsPage })),
);

const CustomersPage = lazy(() =>
  import('../../pages/Customers/CustomersPage').then((module) => ({ default: module.CustomersPage })),
);

const HomePage = lazy(() =>
  import('../../pages/Home/StandardHome/HomePage').then((module) => ({ default: module.HomePage })),
);

const IndicationDetailsPage = lazy(() =>
  import('../../pages/IndicationDetails/IndicationDetailsPage').then((module) => ({
    default: module.IndicationDetailsPage,
  })),
);

const ManagePaymentMethodsPage = lazy(
  () => import('../../pages/Settings/ManagePaymentMethodsPage/ManagePaymentMethodsPage'),
);

const MyProfilePage = lazy(() => import('../../pages/Settings').then((module) => ({ default: module.MyProfilePage })));

const NewQuotePage = lazy(() =>
  import('../../pages/NewQuote/NewQuotePage').then((module) => ({ default: module.NewQuotePage })),
);

const NotificationSettingsPage = lazy(() =>
  import('../../pages/Settings').then((module) => ({ default: module.NotificationSettingsPage })),
);

const ManageSubjectivityLibraryPage = lazy(
  () => import('../../pages/Settings/ManageSubjectivityLibraryPage/ManageSubjectivityLibraryPage'),
);

const QuoteLayout = lazy(() => import('../../pages/QuoteLayout').then((m) => ({ default: m.QuoteLayout })));

const PoliciesPage = lazy(() => import('../../pages/Policies/PoliciesPage'));

const PolicyDetailsPage = lazy(() =>
  import('../../pages/PolicyDetails/PolicyDetailsPage').then((module) => ({ default: module.PolicyDetailsPage })),
);

const QuoteBindInProgressPage = lazy(() =>
  import('../../pages/PortalMessages/QuoteBindInProgressPage').then((module) => ({
    default: module.QuoteBindInProgressPage,
  })),
);

const QuoteDetailsPage = lazy(() =>
  import('../../pages/QuoteDetails/QuoteDetailsPage').then((module) => ({ default: module.QuoteDetailsPage })),
);

const QuotesPage = lazy(() =>
  import('../../pages/Quotes/QuotesPage').then((module) => ({ default: module.QuotesPage })),
);

const SettingsLayout = lazy(() =>
  import('../../pages/Settings').then((module) => ({ default: module.SettingsLayout })),
);

const UnderwriterHomePage = lazy(() => import('../../pages/Home/UnderwriterHome/UnderwriterHomePage'));

const OoseResolutionPage = lazy(() =>
  import('../../pages/OoseResolution/OoseResolutionPage').then((module) => ({
    default: module.OoseResolutionPage,
  })),
);

export const secureRoutes: IRoute[] = [
  {
    roles: Object.values(UserRoles),
    path: '',
    component: ({ isUnderwriter, isInternalUser }: { isUnderwriter: boolean; isInternalUser: boolean }) =>
      isUnderwriter || isInternalUser ? <UnderwriterHomePage /> : <HomePage />,
  },
  {
    roles: Object.values({ ...LioUserRole, ...BrokerageUserRole }),
    path: 'policies',
    component: <PoliciesPage />,
  },
  {
    roles: [...Object.values({ ...LioUserRole, ...BrokerageUserRole }), UserRoles.Quoter],
    path: 'policy/:policyId',
    component: <PolicyDetailsPage />,
  },
  {
    roles: [UserRoles.SystemAdmin, UserRoles.PortalAdmin, UserRoles.Underwriter, UserRoles.SrOps],
    path: 'policy/:policyId/oose-resolution',
    component: <OoseResolutionPage />,
  },
  {
    roles: internalRoles,
    path: 'customers',
    component: <CustomersPage />,
  },
  {
    roles: internalRoles,
    path: 'customer/:customerId',
    component: <CustomerDetailsPage />,
  },
  {
    roles: Object.values({ ...LioUserRole, ...BrokerageUserRole }),
    path: 'quotes',
    component: <QuotesPage />,
  },
  {
    roles: Object.values(UserRoles),
    path: 'quote',
    component: <QuoteLayout />,
    outlets: [
      {
        path: ':quoteId',
        component: <QuoteDetailsPage />,
      },
      {
        path: ':quoteId/review',
        component: <BindQuotePage />,
      },
    ],
  },
  {
    roles: Object.values(UserRoles),
    path: 'indication/:indicationId',
    component: <IndicationDetailsPage />,
  },
  {
    roles: allButLioSales,
    path: 'new-quote',
    component: <NewQuotePage />,
  },
  {
    roles: [...Object.values({ ...LioUserRole, ...BrokerageUserRole }), UserRoles.Quoter],
    path: 'quote/:quoteId/bind-in-progress',
    component: <QuoteBindInProgressPage />,
  },
  {
    roles: allRoles,
    path: 'settings',
    indexPath: 'profile',
    component: <SettingsLayout />,
    outlets: [
      {
        component: <MyProfilePage />,
        roles: [
          UserRoles.Underwriter,
          UserRoles.LIOContractor,
          UserRoles.Producer,
          UserRoles.SrOps,
          UserRoles.SystemAdmin,
          UserRoles.PortalAdmin,
          UserRoles.JrOps,
        ],
      },
      {
        path: 'notification-preferences',
        component: <NotificationSettingsPage />,
      },
      {
        path: 'payment-methods',
        component: <ManagePaymentMethodsPage />,
      },
      {
        path: 'manage-subjectivities',
        component: <ManageSubjectivityLibraryPage />,
      },
      {
        path: 'profile',
        component: <MyProfilePage />,
      },
    ],
  },
  ...paymentRoutes,
  ...adminRoutes,
];
